//@ts-nocheck
import { useEffect, useState } from "react";
import { Table, Button, Input, Switch, message } from "antd";
import { PlusOutlined, SearchOutlined, LeftOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import AddRepresentativeModal from "./addRepresentative";
import { LiaEditSolid } from "react-icons/lia";
import { RiDeleteBinLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import NavigationDropdown from "../../Components/NavigationDropDown";
import PageLogoutButton from "../../Components/PageLogoutButton";

import { getRepresentatives, deleteRepresentative } from "../../utils/API";

// TYPE
interface RepresentativeData {
  id: number;
  name: string;
  email: string;
}

const RepresentativeMaster = () => {
  const navigate = useNavigate();

  const [dataSource, setDataSource] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);

  // FETCH DATA
  const fetchData = async (page = pagination.page, limit = pagination.limit) => {
    try {
      setLoading(true);
      const res = await getRepresentatives(page, limit);
      setDataSource(res.data);
      setPagination({ page: res.page, limit: res.limit || limit, total: res.total, totalPages: res.totalPages });
    } catch (error) {
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, pagination.limit);
  }, []);

  // DELETE
  const handleDelete = async (id: number) => {
    try {
      await deleteRepresentative(id);
      // message.success("Deleted successfully");
      fetchData(pagination.page, pagination.limit);
    } catch {
      message.error("Delete failed");
    }
  };

  // COLUMNS (SAME DESIGN)
  const columns: ColumnsType<RepresentativeData> = [
    {
      title: "ID",
      dataIndex: "id",
      width: "15%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          fontWeight: "600",
          paddingLeft: "40px",
        },
      }),
      onCell: () => ({
        style: { paddingLeft: "40px" },
      }),
    },
    {
      title: "Representative Name",
      dataIndex: "name",
      width: "35%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          fontWeight: "600",
        },
      }),
      render: (val) => val || <span className="text-gray-400">-</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "30%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          fontWeight: "600",
        },
      }),
      render: (val) => val || <span className="text-gray-400">-</span>,
    },
    {
      title: "Action",
      align: "center",
      width: "10%",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#DFEFFF",
          fontWeight: "600",
          paddingRight: "40px",
        },
      }),
      onCell: () => ({
        style: { paddingRight: "40px" },
      }),
      render: (_, record) => (
        <div className="flex justify-center gap-4 text-lg">
          <LiaEditSolid
            className="cursor-pointer hover:text-blue-600"
            onClick={() => {
              setEditData(record);
              setIsModalOpen(true);
            }}
          />
          <RiDeleteBinLine
            className="cursor-pointer hover:text-red-600 text-lg"
            onClick={() => handleDelete(record.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="p-2 bg-slate-50 min-h-screen">
      <div className="flex justify-end mb-2">
        <PageLogoutButton />
      </div>
      {/* HEADER (SAME DESIGN) */}
      <div className="flex justify-between items-center p-2 mb-4 gap-2">
        <div className="flex items-center gap-4">
          <Button
            icon={<LeftOutlined />}
            size="small"
            onClick={() => navigate("/dashboard")}
          />

          <NavigationDropdown />

          <h1 className="text-base sm:text-xl text-gray-700">
            Representative Master
          </h1>
        </div>

        <div className="flex flex-1 justify-end gap-4 items-center">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search ...."
            className="rounded-lg h-10 border-gray-300 flex-1 max-w-xs"
          />

          <Button
            type="primary"
            className="bg-[#004aad] hover:bg-blue-800 rounded-lg text-white h-10 px-6"
            onClick={() => {
              setEditData(null);
              setIsModalOpen(true);
            }}
          >
            <PlusOutlined />
            <span className="ml-2">Add</span>
          </Button>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded-[24px] border p-6 shadow-sm">
        <div className="rounded-xl overflow-hidden mx-3">
          <Table
            dataSource={dataSource}
            columns={columns}
            loading={loading}
            rowKey="id"
            rowClassName={() => "h-16"}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: true,
              onChange: (page, pageSize) => fetchData(page, pageSize),
            }}
          />
        </div>
      </div>

      {/* MODAL */}
      <AddRepresentativeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        refresh={() => fetchData(pagination.page, pagination.limit)}
        editData={editData}
      />
    </div>
  );
};

export default RepresentativeMaster;
